import React, { useState } from 'react';
import { motion } from 'framer-motion';
import './BeneficiaryCard.css';

interface Beneficiary {
  id: string;
  name: string;
  email: string;
  accountNumber: string;
  trustLevel: 'public' | 'semi-private' | 'anonymous';
  lastPayment: string;
  paymentCount: number;
  isVerified: boolean;
  discoveryMethod: string;
  trustScore?: number;
  riskLevel?: 'low' | 'medium' | 'high';
}

interface BeneficiaryCardProps {
  beneficiary: Beneficiary;
  socket: any;
  onAlert?: (message: string, type: string) => void;
  onPayment?: (beneficiary: Beneficiary) => void;
  onShare?: (beneficiary: Beneficiary) => void;
}

const BeneficiaryCard: React.FC<BeneficiaryCardProps> = ({ beneficiary, socket, onAlert, onPayment, onShare }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getTrustIcon = (trustLevel: string) => {
    switch (trustLevel) {
      case 'public': return '🌐';
      case 'semi-private': return '🔒';
      case 'anonymous': return '👤';
      default: return '❓';
    }
  };

  const getRiskColor = (riskLevel?: string) => {
    switch (riskLevel) {
      case 'low': return '#64748b';
      case 'medium': return '#f59e0b';
      case 'high': return '#ef4444';
      default: return '#64748b';
    }
  };

  const handlePay = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPayment?.(beneficiary);
  };

  const handleUpdate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      const response = await fetch('/api/request-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          beneficiaryId: beneficiary.id,
          fields: ['name', 'accountNumber', 'email']
        }),
      });
      
      if (response.ok) {
        onAlert?.('Update request sent to beneficiary', 'info');
      }
    } catch (error) {
      console.error('Error requesting update:', error);
      onAlert?.('Failed to send update request', 'error');
    }
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    onShare?.(beneficiary);
  };

  const handleVerify = async () => {
    try {
      const response = await fetch('/api/verify-beneficiary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          beneficiaryId: beneficiary.id,
          message: `Please verify your details for ${beneficiary.name}`
        }),
      });
      
      if (response.ok) {
        // Show verification sent notification
      }
    } catch (error) {
      console.error('Error sending verification:', error);
    }
  };

  return (
    <motion.div
      className={`beneficiary-card ${beneficiary.riskLevel}`}
      whileHover={{ y: -5, scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300 }}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      {beneficiary.riskLevel === 'high' && (
        <motion.div 
          className="risk-warning"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", delay: 0.2 }}
        >
          ⚠️ High Risk
        </motion.div>
      )}
      
      <div className="card-header">
        <div className="beneficiary-info">
          <div className="beneficiary-avatar">
            {beneficiary.name.split(' ').map(n => n[0]).join('').toUpperCase()}
          </div>
          <div className="beneficiary-details">
            <h3 className="beneficiary-name">{beneficiary.name}</h3>
            <p className="beneficiary-email">{beneficiary.email}</p>
          </div>
        </div>
        
        <div className="trust-indicator">
          <span className="trust-icon">{getTrustIcon(beneficiary.trustLevel)}</span>
          <div className="trust-score">
            <div 
              className="trust-score-bar"
              style={{ 
                width: `${beneficiary.trustScore || 50}%`,
                background: getRiskColor(beneficiary.riskLevel)
              }}
            />
          </div>
        </div>
      </div>

      <div className="card-content">
        <div className="account-info">
          <span className="account-label">Account:</span>
          <span className="account-number">{beneficiary.accountNumber}</span>
        </div>
        
        <div className="stats-row">
          <div className="stat">
            <span className="stat-value">{beneficiary.paymentCount}</span>
            <span className="stat-label">Payments</span>
          </div>
          <div className="stat">
            <span className="stat-value">
              {new Date(beneficiary.lastPayment).toLocaleDateString()}
            </span>
            <span className="stat-label">Last Payment</span>
          </div>
        </div>

        <div className="verification-status">
          {beneficiary.isVerified ? (
            <span className="verified">✅ Verified</span>
          ) : (
            <span className="unverified">⏳ Pending Verification</span>
          )}
        </div>
      </div>

      <motion.div 
        className="card-actions"
        initial={false}
        animate={{ height: isExpanded ? 'auto' : 0, opacity: isExpanded ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="actions-grid">
          <motion.button
            className="action-button primary"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handlePay}
          >
            💸 Pay
          </motion.button>
          
          {beneficiary.trustLevel === 'semi-private' && (
            <motion.button
              className="action-button secondary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                e.stopPropagation();
                handleVerify();
              }}
            >
              🔍 Verify
            </motion.button>
          )}
          
          <motion.button
            className="action-button secondary"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleUpdate}
          >
            🔄 Update
          </motion.button>
          
          <motion.button
            className="action-button secondary"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleShare}
          >
            👥 Share
          </motion.button>
        </div>
      </motion.div>

    </motion.div>
  );
};

export default BeneficiaryCard;
